import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { DailyCatchReport } from 'src/app/data-models/report-models/daily-catch-report.model';
import { LostRecoveredGearForm } from 'src/app/data-models/report-models/lost-recovered-found-gear.model';
import { PauseReport } from 'src/app/data-models/report-models/pause-report.model';
import { SharedMethodsService } from 'src/app/services/shared-methods.service';
import { UserStorageService } from 'src/app/services/storage/user-storage.service';
import { EndReport } from '../../data-models/report-models/end-report.model';
import { MarineMammalInteractionReport } from '../../data-models/report-models/marine-mammal-interaction-form.model';
import { OffloadReport } from '../../data-models/report-models/offload-report.model';
import { SpeciesAtRiskReport } from '../../data-models/report-models/species-at-risk-report.model';
import { Trip } from '../../data-models/trip-models/trip.model';
import { UserData } from '../../data-models/user-models/user-data.model';
import { environment } from 'src/environments/environment';
import { AuthenticationService } from '../../services/authentication.service';
import { ConfirmationDialogComponent } from '../../dialogs/confirmation-dialog/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { OnlineStatusService, OnlineStatusType } from 'ngx-online-status';

type reportRow = {reportId: string, reportType: ReportType, reportStatusMsg: string, reportStatus: string, reportDate: Date, isComplete: boolean};

enum ReportType {
  Start = 'Start', Catch = 'Catch', Pause = 'Pause', End = 'End', Offload = 'Offload',
  LostRecoveredGear = 'Lost/Recovered Gear', MarineMammalInteraction = 'Marine Mammal Interaction', SpeciesAtRisk = 'Species at Risk',
};

@Component({
    selector: 'app-active-trip-home',
    templateUrl: './active-trip-home.component.html',
    styleUrls: ['./active-trip-home.component.css'],
    standalone: false
})


export class ActiveTripHomeComponent implements OnInit {

  private refreshTimerId: any;
  isDataLoaded: boolean = false; // flag that tells us if the HTML is ready to be rendered
  requestToStartNewTrip: boolean = false; // flag that tells us if we should display the confirmation page
  isProduction: boolean = environment.production;

  //data preambles
  userDetails: UserData = new UserData();
  adminUser?: UserData;
  currTrip: Trip = new Trip();
  tableData: reportRow[] = [];
  tableColumns: string[] = ['reportType', 'reportStatus', 'reportDate'];

  isMobile: boolean = window.innerWidth < 550; // threshold for mobile screens

  constructor( // same constructor as original homepage
    private router: Router,
    private dialog: MatDialog,
    private ref: ChangeDetectorRef,
    private authenticationService: AuthenticationService,
    public sharedMethodsService: SharedMethodsService,
    private onlineService: OnlineStatusService
  ) {}


  ngOnInit(): void {
    if (this.onlineService.getStatus() == OnlineStatusType.ONLINE)
      UserStorageService.updateConfirmationNumbers().then((success: boolean) => {
        this.initialize();
        if (success) console.log('Trip data synced')
        else this.sharedMethodsService.toastSessionExpired();    
        if (this.authenticationService.dataError(UserStorageService.error)) return;        
      });
    else this.initialize();
    // start 15-minute refresh timer
    this.refreshTimerId = setTimeout(() => {
      window.location.reload();
    }, 15 * 60 * 1000);
  }

  ngOnDestroy(): void {
    // clear the timer
    if (this.refreshTimerId) {
      clearTimeout(this.refreshTimerId);
    }
  }
  

  initialize() {
    UserStorageService.getCurrTrip().then(() => {
      this.userDetails = UserStorageService.currUser;
      this.adminUser = UserStorageService.adminUser;
      
      // Guard: Check if user has an active logbook
      if (!this.hasActiveLogbook() && !this.adminUser) {
        this.userDetails.currHomepage = 'no-trip-home';
        UserStorageService.saveUser(this.userDetails).then(() => {
          this.router.navigate(['no-trip-home']);
        });
        return;
      }
    }).then(() => {
      this.currTrip = UserStorageService.currTrip;

      // Initialize an empty array for reports except the start report
      let otherReports = [];

      // Add the start report as the first record
      let startReport = this.currTrip.startFishingReport;
      this.tableData.push({
        reportId: startReport.internalId,
        reportType: ReportType.Start,
        reportStatusMsg: startReport.confirmationNumber,
        reportStatus: SharedMethodsService.getReportStatus(startReport.confirmationNumber),
        reportDate: startReport.startDate,
        isComplete: startReport.isComplete
      });

      // Collect the other reports in an array to sort them later
      this.currTrip.dailyCatchReports.forEach((report: DailyCatchReport) => {
        otherReports.push({
          reportId: report.internalId,
          reportType: ReportType.Catch,
          reportStatusMsg: report.confirmationNumber,
          reportStatus: SharedMethodsService.getReportStatus(report.confirmationNumber),
          reportDate: report.dateFished,
          isComplete: report.isComplete
        });
      });

      this.currTrip.pauseFishingReports.forEach((report: PauseReport) => {
        otherReports.push({
          reportId: report.internalId,
          reportType: ReportType.Pause,
          reportStatusMsg: report.confirmationNumber,
          reportStatus: SharedMethodsService.getReportStatus(report.confirmationNumber),
          reportDate: report.pauseDate,
          isComplete: report.isComplete
        });
      });

      this.currTrip.speciesAtRiskReports.forEach((report: SpeciesAtRiskReport) => {
        otherReports.push({
          reportId: report.internalId,
          reportType: ReportType.SpeciesAtRisk,
          reportStatusMsg: "",
          reportStatus: "",
          reportDate: report.encounterDate,
          isComplete: report.isSubmitted
        });
      });

      this.currTrip.marineMammalInteractionReports.forEach((report: MarineMammalInteractionReport) => {
        otherReports.push({
          reportId: report.internalId,
          reportType: ReportType.MarineMammalInteraction,
          reportStatusMsg: "",
          reportStatus: "",
          reportDate: report.reportDate,
          isComplete: report.isSubmitted
        });
      });

      this.currTrip.lostRecoveredFoundGearReports.forEach((report: LostRecoveredGearForm) => {
        otherReports.push({
          reportId: report.internalId,
          reportType: ReportType.LostRecoveredGear,
          reportStatusMsg: "",
          reportStatus: "",
          reportDate: report.reportDate,
          isComplete: report.isSubmitted
        });
      });

      this.currTrip.offloadReports.forEach((report: OffloadReport) => {
        otherReports.push({
          reportId: report.internalId,
          reportType: ReportType.Offload,
          reportStatusMsg: report.confirmationNumber,
          reportStatus: SharedMethodsService.getReportStatus(report.confirmationNumber),
          reportDate: report.offloadDate,
          isComplete: report.isComplete
        });
      });

      if (this.currTrip.endFishingReport.isComplete) {
        let endReport = this.currTrip.endFishingReport;
        otherReports.push({
          reportId: endReport.internalId,
          reportType: ReportType.End,
          reportStatusMsg: endReport.confirmationNumber,
          reportStatus: SharedMethodsService.getReportStatus(endReport.confirmationNumber),
          reportDate: endReport.endDate,
          isComplete: endReport.isComplete
        });
      }

      // Sort the other reports by reportDate
      otherReports.sort((a, b) => new Date(a.reportDate).getTime() - new Date(b.reportDate).getTime());

      // Append the sorted reports to tableData, keeping the start report at the top
      this.tableData.push(...otherReports);

      this.isDataLoaded = true; // set flag to render HTML
      this.ref.detectChanges(); // re-render HTML
    });
  }



  clickRow(row: reportRow) {
    if (row.reportType == ReportType.Catch) {
      switch (this.currTrip.logbook.gearName) {
        case 'Gill Net':
          this.router.navigate(['gill-net-report', row.reportId], { queryParams: { status: row.isComplete ? 'complete' : 'edit' } });
          break;
        case 'Seine':
          this.router.navigate(['seine-report', row.reportId], { queryParams: { status: row.isComplete ? 'complete' : 'edit' } });
          break;
        case 'Troll':
          this.router.navigate(['troll-report', row.reportId], { queryParams: { status: row.isComplete ? 'complete' : 'edit' } });
          break;
        default:
          this.sharedMethodsService.toastSomethingWentWrong();
          break;
      }
    } else {
      switch (row.reportType) {
        case ReportType.Start:
          this.router.navigate(['start-report', row.reportId], { queryParams: { status: row.isComplete ? 'complete' : 'edit' } });
          break;
        case ReportType.Pause:
          this.router.navigate(['pause-report', row.reportId], { queryParams: { status:  row.isComplete ? 'complete' : 'edit'} });
          break;
        case ReportType.End:
          this.router.navigate(['end-report', row.reportId], { queryParams: { status: row.isComplete ? 'complete' : 'edit' } });
          break;
        case ReportType.Offload:
          this.router.navigate(['offload-report', row.reportId], { queryParams: { status: row.isComplete ? 'complete' : 'edit' } });
          break;
        case ReportType.LostRecoveredGear:
          this.router.navigate(['lost-recovered-gear-form',  row.reportId], { queryParams: { status:  row.isComplete ? 'complete' : 'edit'} });
          break;
        case ReportType.SpeciesAtRisk:
          this.router.navigate(['species-at-risk-report', row.reportId], { queryParams: { status: row.isComplete ? 'complete' : 'edit' } });
          break;
        case ReportType.MarineMammalInteraction:
          this.router.navigate(['marine-mammal-interaction-form', row.reportId], { queryParams: { status: row.isComplete ? 'complete' : 'edit' } });
          break;

      }
    }
  }

  
  /*  ---------- Report Navigation Methods  ---------- */


  // go to the catch report provided in the parameter or display an error if no match
  toCatchReport(): void {

    let newCatchReport = new DailyCatchReport();
    this.currTrip.dailyCatchReports.push(newCatchReport);

    UserStorageService.saveTrip(this.currTrip, true, false).then(() => {
      switch(this.currTrip.logbook.gearName){
        case 'Gill Net':
          this.router.navigate(['gill-net-report', newCatchReport.internalId], { queryParams: { status:  'new'} });
          break;
        case 'Seine':
          this.router.navigate(['seine-report',  newCatchReport.internalId], { queryParams: { status:  'new'} });
          break;
        case 'Troll':
          this.router.navigate(['troll-report',  newCatchReport.internalId], { queryParams: { status:  'new'} });
          break;
        default:
          this.sharedMethodsService.toastSomethingWentWrong();
          break;
      }
    });

  }


  toCancelReport() {
    if (this.currTrip.dailyCatchReports.some(dcr => !dcr.isComplete) || this.currTrip.pauseFishingReports.some(pr => !pr.isComplete)) {
      this.currTrip.dailyCatchReports = this.currTrip.dailyCatchReports.filter(dcr => dcr.isComplete);
      this.currTrip.pauseFishingReports = this.currTrip.pauseFishingReports.filter(pr => pr.isComplete);
      UserStorageService.saveTrip(this.currTrip, true, false).then(() => { //save the trip then open the cancel report
        this.router.navigate(['cancel-report']);
      });
    } else {
      this.router.navigate(['cancel-report']);
    }

  }


  toPauseReport(): void {
    let newPauseReport = new PauseReport();
    this.currTrip.pauseFishingReports.push(newPauseReport);

    UserStorageService.saveTrip(this.currTrip, true, false).then(() => {
      this.router.navigate(['pause-report', newPauseReport.internalId], { queryParams: { status:  'new'} });
    });
  }

  toEndReport(): void {

    if (this.currTrip.dailyCatchReports.some(dcr => !dcr.isComplete) || this.currTrip.pauseFishingReports.some(pr => !pr.isComplete)) {

      this.dialog.open(ConfirmationDialogComponent, {
        data: { screen: 'incomplete_reports' }
      }).afterClosed().subscribe(result => {
        if (result) {
          this.currTrip.dailyCatchReports = this.currTrip.dailyCatchReports.filter(dcr => dcr.isComplete);
          this.currTrip.pauseFishingReports = this.currTrip.pauseFishingReports.filter(pr => pr.isComplete);
          this.startEndReport();
        }
      });
    } else {
      this.startEndReport();
    }

  }

  private startEndReport(): void {

    this.currTrip.endFishingReport = new EndReport();
    UserStorageService.saveTrip(this.currTrip, true, false).then(() => //save the trip then open the end report
      this.router.navigate(['end-report', this.currTrip.endFishingReport.internalId], { queryParams: { status: 'new' } }));

  }


  // go to an offload report
  toOffloadReport(): void {

    let newOffloadReport = new OffloadReport();
    this.currTrip.offloadReports.push(newOffloadReport);

    UserStorageService.saveTrip(this.currTrip, true, false).then(() => {
      this.router.navigate(['offload-report', newOffloadReport.internalId], { queryParams: { status: 'new' } });
    });
    
  }


  // go to a species at risk report
  toSpeciesAtRiskReport(): void {

    let newSpeciesAtRiskReport = new SpeciesAtRiskReport();
    this.currTrip.speciesAtRiskReports.push(newSpeciesAtRiskReport);
    UserStorageService.saveTrip(this.currTrip).then(() => this.router.navigate(['species-at-risk-report', newSpeciesAtRiskReport.internalId], { queryParams: { status: 'new' } }));

  }


  toLostRecoveredGearForm(): void {

    let newGearForm = new LostRecoveredGearForm();
    this.currTrip.lostRecoveredFoundGearReports.push(newGearForm);
    UserStorageService.saveTrip(this.currTrip).then(() => this.router.navigate(['lost-recovered-gear-form', newGearForm.internalId], { queryParams: { status: 'new' } }));
  }


  toMarineMammalInteractionForm(): void {

    let newMarineMammalInteractionReport = new MarineMammalInteractionReport();
    this.currTrip.marineMammalInteractionReports.push(newMarineMammalInteractionReport);
    UserStorageService.saveTrip(this.currTrip).then(() => 
    this.router.navigate(['marine-mammal-interaction-form', newMarineMammalInteractionReport.internalId], { queryParams: { status: 'new' } }));
  }


  // go to the new trip homepage, deleting any incomplete reports left in the
  // trip (the user has already confirmed this in setNewTripFlag)
  toNoTripHome(): void {

    if (this.currTrip.dailyCatchReports.some(r => !r.isComplete)
      || this.currTrip.pauseFishingReports.some(r => !r.isComplete)
      || this.currTrip.offloadReports.some(r => !r.isComplete)) {
      this.currTrip.dailyCatchReports = this.currTrip.dailyCatchReports.filter(r => r.isComplete);
      this.currTrip.pauseFishingReports = this.currTrip.pauseFishingReports.filter(r => r.isComplete);
      this.currTrip.offloadReports = this.currTrip.offloadReports.filter(r => r.isComplete);
      UserStorageService.saveTrip(this.currTrip, true, false).then(() => this.navigateToNoTripHome());
    } else {
      this.navigateToNoTripHome();
    }
  }

  private navigateToNoTripHome(): void {
    this.userDetails.currHomepage = 'no-trip-home';
    UserStorageService.saveUser(this.userDetails).then(() => {
      this.router.navigate([this.userDetails.currHomepage]);
    });
  }

  /*  ---------- Helper Methods  ---------- */


  setNewTripFlag(): void {

    // If any submitted report in the trip (start, all catch/pause, end, and
    // any offload reports) is still awaiting a DFO response or has an error,
    // warn the user that no further submissions will be possible for this
    // trip once a new one is started, but let them continue. Reports that
    // were never finished ("Incomplete") don't block a new trip either, but
    // the user must confirm they will be deleted.
    const statuses = this.reportStatuses();

    if (statuses.some(status => status !== 'Accepted' && status !== 'Incomplete')) {
      this.dialog.open(ConfirmationDialogComponent, {
        data: { screen: 'unconfirmed_reports' }
      }).afterClosed().subscribe(result => {
        if (result) this.confirmIncompleteReports(statuses);
      });
      return;
    }

    this.confirmIncompleteReports(statuses);

  }


  // Second step of starting a new trip: if the trip has incomplete reports,
  // the user must confirm they will be deleted before the new trip starts.
  private confirmIncompleteReports(statuses: string[]): void {

    if (statuses.includes('Incomplete')) {
      this.dialog.open(ConfirmationDialogComponent, {
        data: { screen: 'new_trip_incomplete_reports' }
      }).afterClosed().subscribe(result => {
        if (result) this.requestToStartNewTrip = true;
      });
      return;
    }

    this.requestToStartNewTrip = true;

  }


  // Returns the submission status of every report in the trip that requires a
  // DFO confirmation number ('Accepted', 'Incomplete', 'Not submitted',
  // 'Awaiting response', or 'Error with submission').
  reportStatuses(): string[] {

    const confirmationNumbers: string[] = [
      this.currTrip.startFishingReport.confirmationNumber,
      this.currTrip.endFishingReport.confirmationNumber,
      ...this.currTrip.dailyCatchReports.map(r => r.confirmationNumber),
      ...this.currTrip.pauseFishingReports.map(r => r.confirmationNumber),
      ...this.currTrip.offloadReports.map(r => r.confirmationNumber),
    ];

    return confirmationNumbers.map(
      confirmationNumber => SharedMethodsService.getReportStatus(confirmationNumber ?? '')
    );

  }


  clearNewTripFlag(): void {

    this.requestToStartNewTrip = false;

  }

  // Checks if the user has an active registered logbook
  hasActiveLogbook(): boolean {
    return this.userDetails.logbooks.some(lb => lb.isRegistered && (lb.isActive ?? true));
  }

  // Checks if a complete report exists in the array of reports provided
  // Used for determining which reports should be available to the user
  hasCompleteReport(reports: any[]): boolean {
    return reports.some(r => r.isComplete);
  }

}
